import type { HTMLAttributes } from 'react';
import { cn } from '../../lib/cn';
import { formatPercent, NO_DATA_DASH } from '../../lib/format';
import { ActivityHeatmap, type DiaAtividade } from './ActivityHeatmap';

/**
 * FAIXA DE UPTIME — a faixa de 30 dias do monitorador com o "% conectado" em cima (D6).
 *
 * Extraída da gaveta do monitorador (`ActivityHeatmap` + frase), para o card da lista e a
 * gaveta dizerem a MESMA coisa: *"Conectado 98,7% dos últimos 30 dias"*.
 *
 * - A fração chega pronta do servidor (ponderada por medição, não por dia — ver
 *   `uptimeDaFaixa` em `packages/db/src/monitor-health.ts`). O componente não conta.
 * - `fraction = null` (nenhuma medição) mostra "—" e a frase diz que ainda não medimos:
 *   não é 100% nem 0% (invariantes 8 e 12).
 * - Dia sem medição fica hachurado e a frase conta quantos foram ("2 dias sem registro").
 */
export interface UptimeStripProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  dias: readonly DiaAtividade[];
  /** 0 a 1, pronto do servidor. `null` = nada medido. */
  fraction: number | null;
  /** Dias da janela sem registro nenhum. */
  daysWithoutRecord: number;
  /** Para leitor de tela. */
  label?: string;
  size?: 'sm' | 'md';
  legenda?: boolean;
}

export function UptimeStrip({
  dias,
  fraction,
  daysWithoutRecord,
  label = 'Conectividade do monitorador',
  size = 'sm',
  legenda = true,
  className,
  ...props
}: UptimeStripProps) {
  const janela = dias.length;

  return (
    <div className={cn('flex flex-col gap-1.5', className)} {...props}>
      <p className="text-xs text-muted-foreground">
        {fraction === null ? (
          <>
            <span className="nums text-foreground" aria-hidden="true">
              {NO_DATA_DASH}
            </span>{' '}
            Ainda não medimos este número{janela > 0 ? ` nos últimos ${janela} dias` : ''}.
          </>
        ) : (
          <>
            Conectado{' '}
            {/*
             * Sem cor de julgamento: não há régua calibrada de "uptime bom" para número de
             * WhatsApp (mesma regra do `Meter`). Quem diz que caiu é a faixa, dia a dia.
             */}
            <span className="nums font-medium text-foreground">{formatPercent(fraction, 1)}</span>{' '}
            dos últimos {janela} dias
            {daysWithoutRecord > 0
              ? ` · ${daysWithoutRecord} ${daysWithoutRecord === 1 ? 'dia' : 'dias'} sem registro`
              : ''}
          </>
        )}
      </p>
      {janela > 0 ? (
        <ActivityHeatmap dias={dias} label={label} size={size} legenda={legenda} />
      ) : null}
    </div>
  );
}
